/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'
import {defineMessage, useIntl} from 'react-intl'
import {
    IconButton,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import TrackOrderForm from '../components/track-order-form'
import {trackOrderModalCloseButtonStyle} from '../components/track-order-form/index.style'
import {fetchWrapper} from '../utills/fetchWrapper'
import {pxToRem} from '../utills/pxToRem'

const MISMATCH_ERROR = defineMessage({
    defaultMessage:
        'Sorry this order number, email or postal code does not match our records. Check your records and try again.',
    id: 'track_order_modal.error.data_mismatch'
})

export const TrackOrderModal = ({isOpen, onOpen, onClose, ...props}) => {
    const {buildUrl} = useMultiSite()
    const {formatMessage} = useIntl()
    const [mt, setMt] = useState(64)

    const navigate = useNavigation()
    const form = useForm()

    // Reset form and local state when opening the modal
    useEffect(() => {
        if (isOpen) {
            form.reset()
        }
        const header = document.querySelector('#sticky-header')
        const hEl = header?.parentNode
        if (hEl) {
            const hElHeight = hEl?.offsetHeight
            setMt(hElHeight)
        }
    }, [isOpen])

    const fetchOrderDetail = async (urlEncodedParams) => {
        const apiUrl = `/api/order-details?${urlEncodedParams}`
        const response = await fetchWrapper(apiUrl)
            .then((response) => {
                if (response.status === 404) {
                    const message = formatMessage(MISMATCH_ERROR)
                    form.setError('global', {type: 'manual', message})
                }
                if (response.status === 500) {
                    const message = formatMessage({
                        defaultMessage: 'Something went wrong. Try again!',
                        id: 'global.error.something_went_wrong'
                    })
                    form.setError('global', {type: 'manual', message})
                }
                return response.json()
            })
            .catch((err) => {
                console.log(err)
                const message = formatMessage({
                    defaultMessage: 'Something went wrong. Try again!',
                    id: 'global.error.something_went_wrong'
                })
                form.setError('global', {type: 'manual', message})
            })
        return response
    }

    const submitForm = async (data) => {
        form.clearErrors()
        const urlEncodedParams = new URLSearchParams({
            orderNo: data.orderNumber,
            email: data.orderEmail,
            zipCode: data.zipCode
        })
        const {data: order} = await fetchOrderDetail(urlEncodedParams)
        if (order?.orderNo) {
            const path = buildUrl(`/guest/orders/${order?.orderNo}?${urlEncodedParams}`)
            navigate(path)
            onClose()
        }
    }

    return (
        <Modal
            size={{base: 'md', lg: 'md'}}
            closeOnOverlayClick={false}
            data-testid="sf-track-order-modal"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            isCentered
            {...props}
        >
            <ModalOverlay />
            <ModalContent mt={pxToRem(mt)}>
                <IconButton
                    icon={
                        <img
                            src={getAssetUrl('static/img/pdp/close.svg')}
                            height="15"
                            width="15"
                            alt="close"
                        />
                    }
                    variant="unstyled"
                    onClick={onClose}
                    sx={trackOrderModalCloseButtonStyle}
                />
                <ModalBody p={0} bg="white">
                    <TrackOrderForm form={form} submitForm={submitForm} />
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

TrackOrderModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func,
    onClose: PropTypes.func
}

export const useTrackOrderModal = () => {
    const {isOpen, onOpen, onClose} = useDisclosure()

    return {
        isOpen,
        onOpen,
        onClose
    }
}
